import { useState, useRef, useEffect, useCallback, useId } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import './ScreenshotViewer.css'

// Replay screenshot URLs embed the capture point as a millisecond offset into the recording, e.g.
// `.../analysis/screenshot-12300.jpg`. Parse that offset so we can show when, within the recording,
// the frame was captured. Returns null for any URL that doesn't match the pattern (e.g. uploaded
// images), so callers simply render no badge.
export function screenshotOffsetMs(url: string | null | undefined): number | null {
  if (!url) return null
  const match = url.match(/screenshot-(\d+)\.(?:jpe?g|png|webp|gif)(?:[?#]|$)/i)
  if (!match) return null
  const ms = Number(match[1])
  return Number.isFinite(ms) ? ms : null
}

// Format a recording offset (ms) as a compact `M:SS.s` timecode, e.g. 12300 → "0:12.3".
function formatRecordingOffset(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const tenths = Math.floor((ms % 1000) / 100)
  return `${minutes}:${String(seconds).padStart(2, '0')}.${tenths}`
}

// Subtle timecode badge pinned to the corner of a screenshot, indicating when in the
// recording the frame was captured. Renders nothing when the URL carries no parseable offset.
function ScreenshotTimestamp({ url }: { url: string | null | undefined }) {
  const ms = screenshotOffsetMs(url)
  if (ms == null) return null
  const label = formatRecordingOffset(ms)
  return (
    <span
      className="screenshot-viewer__timestamp"
      title={`Captured at ${label} into the recording`}
      data-testid="screenshot-timestamp"
    >
      {label}
    </span>
  )
}

interface ScreenshotViewerProps {
  frame: string
  alt: string
  boxStyle?: CSSProperties | null
  circleStyle?: CSSProperties | null
  testIdPrefix?: string
  onError?: () => void
  fillViewport?: boolean
}

function ScreenshotViewerFrame({
  frame,
  alt,
  boxStyle,
  circleStyle,
  testIdPrefix,
  fillViewport = false,
  onError,
}: ScreenshotViewerProps) {
  const [aspectRatio, setAspectRatio] = useState(1)
  return (
    <div
      className="screenshot-viewer__frame"
      style={fillViewport ? { width: `min(100%, calc((100dvh - 72px) * ${aspectRatio}))` } : undefined}
    >
      <img
        src={frame}
        onError={onError}
        alt={alt}
        loading={fillViewport ? 'eager' : 'lazy'}
        onLoad={(event) => {
          const image = event.currentTarget
          if (image.naturalHeight) setAspectRatio(image.naturalWidth / image.naturalHeight)
        }}
      />
      {boxStyle && (
        <div
          className="screenshot-viewer__box"
          style={boxStyle}
          data-testid={testIdPrefix ? `${testIdPrefix}-box` : undefined}
          aria-hidden="true"
        />
      )}
      {circleStyle && (
        <div
          className="screenshot-viewer__circle"
          style={circleStyle}
          data-testid={testIdPrefix ? `${testIdPrefix}-circle` : undefined}
          aria-hidden="true"
        />
      )}
      <ScreenshotTimestamp url={frame} />
    </div>
  )
}

// Portals keep the preview and full-screen viewer above their container's overflow boundaries.
export default function ScreenshotViewer({ frame, alt, boxStyle, circleStyle, testIdPrefix, onError }: ScreenshotViewerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<HTMLDialogElement>(null)
  const [expanded, setExpanded] = useState(false)
  const closeTimeoutRef = useRef<number | null>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const cardId = useId()
  const viewerId = useId()

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    const card = cardRef.current
    if (!trigger || !card) return

    const triggerRect = trigger.getBoundingClientRect()
    const cardRect = card.getBoundingClientRect()
    const viewportPadding = 16
    const gap = 8
    const left = Math.max(
      viewportPadding,
      Math.min(triggerRect.left, window.innerWidth - cardRect.width - viewportPadding),
    )
    const belowTop = triggerRect.bottom + gap
    const top = belowTop + cardRect.height <= window.innerHeight - viewportPadding
      ? belowTop
      : Math.max(viewportPadding, triggerRect.top - cardRect.height - gap)

    setPosition({ top, left })
  }, [])

  const openCard = useCallback(() => {
    clearCloseTimeout()
    if (!expanded) setOpen(true)
  }, [clearCloseTimeout, expanded])

  const expandScreenshot = () => {
    clearCloseTimeout()
    setOpen(false)
    setPosition(null)
    setExpanded(true)
  }

  const closeCard = useCallback(() => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpen(false)
      setPosition(null)
    }, 120)
  }, [clearCloseTimeout])

  useEffect(() => {
    if (!open) return
    const frameId = window.requestAnimationFrame(updatePosition)
    const handleViewportChange = () => updatePosition()
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)
    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [open, updatePosition])

  useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout])

  useEffect(() => {
    if (!expanded) return
    const viewer = viewerRef.current
    viewer?.showModal()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      viewer?.close()
      document.body.style.overflow = previousOverflow
    }
  }, [expanded])

  const preview = open && !expanded && createPortal(
    <div
      ref={cardRef}
      id={cardId}
      className="screenshot-viewer__hovercard"
      role="dialog"
      aria-label="Screenshot preview"
      onMouseEnter={openCard}
      onMouseLeave={closeCard}
      onFocus={openCard}
      onBlur={closeCard}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          closeCard()
          triggerRef.current?.focus()
        }
      }}
      style={position ? { top: position.top, left: position.left } : { top: 0, left: 0, visibility: 'hidden' }}
      data-testid="chronology-screenshot-hovercard"
    >
      <button
        type="button"
        className="screenshot-viewer__hovercard-zoom"
        aria-label={`${alt}. Click to expand screenshot`}
        aria-haspopup="dialog"
        onClick={expandScreenshot}
      >
        <ScreenshotViewerFrame onError={onError} frame={frame} alt={`${alt} preview`} boxStyle={boxStyle} circleStyle={circleStyle} />
      </button>
    </div>,
    document.body,
  )

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="screenshot-viewer__thumbnail"
        aria-label={`${alt}. Click to expand screenshot`}
        aria-controls={expanded ? viewerId : cardId}
        aria-expanded={open || expanded}
        aria-haspopup="dialog"
        onClick={expandScreenshot}
        onMouseEnter={openCard}
        onMouseLeave={closeCard}
        onFocus={openCard}
        onBlur={closeCard}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            closeCard()
            triggerRef.current?.blur()
          }
        }}
        data-testid="chronology-screenshot-thumbnail"
      >
        <ScreenshotViewerFrame onError={onError} frame={frame} alt={alt} boxStyle={boxStyle} circleStyle={circleStyle} testIdPrefix={testIdPrefix} />
      </button>
      {preview}
      {expanded && createPortal(
        <dialog
          ref={viewerRef}
          id={viewerId}
          className="screenshot-viewer__dialog"
          aria-label="Expanded screenshot"
          onClose={() => {
            triggerRef.current?.focus()
            setExpanded(false)
          }}
          data-testid="chronology-screenshot-viewer"
        >
          <button
            type="button"
            className="screenshot-viewer__dialog-close"
            aria-label="Close screenshot"
            onClick={() => viewerRef.current?.close()}
          >
            <X size={24} aria-hidden="true" />
          </button>
          <ScreenshotViewerFrame onError={onError} frame={frame} alt={alt} boxStyle={boxStyle} circleStyle={circleStyle} fillViewport />
        </dialog>,
        document.body,
      )}
    </>
  )
}

